import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatDisplayDate, formatDisplayDateTime } from "../../utils/dateFormat";
import { Link2, Mail, RefreshCw, Eye, Copy, Check, Plus, Trash2, ExternalLink, Settings, Save, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { PageInfoUnderTabs } from "./PageInfo";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import ConfirmModal from "../ui/ConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration
const STATUSES = {
  paid: { label: "Pagato", color: "bg-green-100 text-green-800" },
  confirmed: { label: "Confermato", color: "bg-blue-100 text-blue-800" },
  pending: { label: "In attesa", color: "bg-yellow-100 text-yellow-800" },
  pending_payment: { label: "Pagamento in attesa", color: "bg-orange-100 text-orange-800" },
  cancelled: { label: "Cancellato", color: "bg-red-100 text-red-800" },
  expired: { label: "Scaduto", color: "bg-gray-100 text-gray-800" },
  failed: { label: "Fallito", color: "bg-red-100 text-red-800" },
  unpaid: { label: "Non pagato", color: "bg-yellow-100 text-yellow-800" }
};

const StatusBadge = ({ status }) => {
  const config = STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

export default function PaymentLinkPage({ getAuthHeaders }) {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "create";
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Form state
  const [newLink, setNewLink] = useState({
    amount: "",
    currency: "EUR",
    customer_name: "",
    customer_email: "",
    booking_id: "",
    check_in_date: "",
    check_out_date: "",
    adults: 0,
    children: 0,
    infants: 0,
    children_ages: "",
    tourist_tax_total: null
  });
  
  const [creatingLink, setCreatingLink] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Refs for scroll and focus
  const successBoxRef = useRef(null);
  const linkInputRef = useRef(null);
  
  // Links list state
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingLinkId, setDeletingLinkId] = useState(null);
  const [deletingBulk, setDeletingBulk] = useState(false);
  
  // Platform config state
  const [config, setConfig] = useState({
    provider: "stripe",
    mode: "test",
    currency: "EUR",
    country: "IT",
    notification_email: "",
    stripe_configured: false
  });
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Stripe Credentials state
  const [stripeCredentials, setStripeCredentials] = useState(null);
  const [stripeTestPublishable, setStripeTestPublishable] = useState("");
  const [stripeTestSecret, setStripeTestSecret] = useState("");
  const [stripeTestWebhook, setStripeTestWebhook] = useState("");
  const [stripeLivePublishable, setStripeLivePublishable] = useState("");
  const [stripeLiveSecret, setStripeLiveSecret] = useState("");
  const [stripeLiveWebhook, setStripeLiveWebhook] = useState("");
  const [savingCredentials, setSavingCredentials] = useState(false);
  
  // Email defaults
  const DEFAULT_EMAIL_SUBJECT = "Reservation Payment - Suite Lucina";
  const DEFAULT_EMAIL_TEMPLATE = `Dear (customer name),

Thank you for choosing Suite Lucina for your stay in Rome. You may proceed with the payment for your reservation with check-in on [check-in date] and check-out on [check-out date] by clicking the following link:

(link)

Kind regards,
Suite Lucina.`;
  
  const [emailSubject, setEmailSubject] = useState(DEFAULT_EMAIL_SUBJECT);
  const [emailTemplate, setEmailTemplate] = useState(DEFAULT_EMAIL_TEMPLATE);
  
  // Confirmation modals state (non-destructive)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: "",
    message: "",
    variant: "question",
    onConfirm: () => {}
  });
  
  // Fetch links and config on mount
  // Update tab when URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);
  
  useEffect(() => {
    fetchConfig();
    fetchStripeCredentials();
  }, []);
  
  useEffect(() => {
    if (activeTab === "list") {
      fetchLinks();
    }
  }, [activeTab]);
  
  // Fetch config
  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/payments/config`, getAuthHeaders());
      setConfig(res.data);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  };
  
  // Fetch Stripe credentials
  const fetchStripeCredentials = async () => {
    try {
      const res = await axios.get(`${API}/admin/stripe/credentials`, getAuthHeaders());
      setStripeCredentials(res.data);
      // Update config mode based on credentials
      if (res.data.active_mode) {
        setConfig(prev => ({...prev, mode: res.data.active_mode}));
      }
    } catch (error) {
      console.error("Error fetching Stripe credentials:", error);
    }
  };
  
  // Save Stripe credentials
  const saveStripeCredentials = async () => {
    // Show confirmation for secrets
    const hasSecrets = stripeTestSecret || stripeLiveSecret || stripeTestWebhook || stripeLiveWebhook;
    if (hasSecrets) {
      setConfirmModalConfig({
        title: "Salvataggio Credenziali",
        message: "Le Secret Key e i Webhook Secret non saranno più visualizzabili dopo il salvataggio. Si consiglia di salvare le credenziali in una cartella sicura.",
        variant: "warning",
        onConfirm: () => {
          setConfirmModalOpen(false);
          doSaveStripeCredentials();
        }
      });
      setConfirmModalOpen(true);
      return;
    }
    
    doSaveStripeCredentials();
  };
  
  const doSaveStripeCredentials = async () => {
    setSavingCredentials(true);
    try {
      const payload = {};
      if (stripeTestPublishable) payload.publishable_key_test = stripeTestPublishable;
      if (stripeTestSecret) payload.secret_key_test = stripeTestSecret;
      if (stripeTestWebhook) payload.webhook_secret_test = stripeTestWebhook;
      if (stripeLivePublishable) payload.publishable_key_live = stripeLivePublishable;
      if (stripeLiveSecret) payload.secret_key_live = stripeLiveSecret;
      if (stripeLiveWebhook) payload.webhook_secret_live = stripeLiveWebhook;
      
      await axios.put(`${API}/admin/stripe/credentials`, payload, getAuthHeaders());
      toast.success("Credenziali Stripe salvate!");
      
      // Clear input fields
      setStripeTestPublishable("");
      setStripeTestSecret("");
      setStripeTestWebhook("");
      setStripeLivePublishable("");
      setStripeLiveSecret("");
      setStripeLiveWebhook("");
      
      // Refresh credentials
      fetchStripeCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingCredentials(false);
    }
  };
  
  // Save config
  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.post(`${API}/admin/payments/config`, config, getAuthHeaders());
      toast.success("Configurazione salvata!");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingConfig(false);
    }
  };
  
  // Handle mode change
  const handleModeChange = async (newMode) => {
    if (newMode === "live") {
      if (!stripeCredentials?.has_live_credentials) {
        toast.error("Configura prima le credenziali LIVE");
        return;
      }
      // Use styled modal confirmation
      setConfirmModalConfig({
        title: "Attivare Modalità LIVE",
        message: "Stai per attivare la modalità LIVE. I pagamenti saranno REALI e verranno addebitati sulle carte dei clienti.",
        variant: "warning",
        onConfirm: async () => {
          setConfirmModalOpen(false);
          try {
            await axios.put(`${API}/admin/stripe/credentials`, { active_mode: newMode }, getAuthHeaders());
            setConfig({...config, mode: newMode});
            fetchStripeCredentials();
            toast.success(`Modalità ${newMode.toUpperCase()} attivata`);
          } catch (error) {
            toast.error(error.response?.data?.detail || "Errore nel cambio modalità");
          }
        }
      });
      setConfirmModalOpen(true);
      return;
    }
    
    try {
      await axios.put(`${API}/admin/stripe/credentials`, { active_mode: newMode }, getAuthHeaders());
      setConfig({...config, mode: newMode});
      fetchStripeCredentials();
      toast.success(`Modalità ${newMode.toUpperCase()} attivata`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel cambio modalità");
    }
  };
  
  // Fetch links
  const fetchLinks = async () => {
    setLoadingLinks(true);
    try {
      const res = await axios.get(`${API}/admin/payments/links`, getAuthHeaders());
      setLinks(res.data.links || []);
    } catch (error) {
      toast.error("Errore nel caricamento dei link");
    } finally {
      setLoadingLinks(false);
    }
  };
  
  // Format date - use centralized utility
  const formatDateDisplay = (dateStr) => {
    return formatDisplayDateTime(dateStr);
  };
  
  // Generate email preview
  const getEmailPreview = () => {
    let preview = emailTemplate;
    const customerName = newLink.customer_name || "(customer name)";
    const checkIn = newLink.check_in_date || "[check-in date]";
    const checkOut = newLink.check_out_date || "[check-out date]";
    const linkPlaceholder = createdLink?.short_url || "https://...../api/pay/xxxxx";
    
    preview = preview.replace(/\(customer name\)/g, customerName);
    preview = preview.replace(/\[check-in date\]/g, checkIn);
    preview = preview.replace(/\[check-out date\]/g, checkOut);
    preview = preview.replace(/\(link\)/g, linkPlaceholder);
    
    return preview;
  };
  
  // Reset form
  const resetForm = () => {
    setNewLink({
      amount: "",
      currency: "EUR",
      customer_name: "",
      customer_email: "",
      booking_id: "",
      check_in_date: "",
      check_out_date: "",
      adults: 0,
      children: 0,
      infants: 0,
      children_ages: "",
      tourist_tax_total: null
    });
    setEmailSubject(DEFAULT_EMAIL_SUBJECT);
    setEmailTemplate(DEFAULT_EMAIL_TEMPLATE);
    setCreatedLink(null);
    setCopied(false);
  };
  
  // Create payment link
  const createPaymentLink = async (e) => {
    if (e) e.preventDefault();
    
    // Validation
    if (!newLink.amount || parseFloat(newLink.amount) <= 0) {
      toast.error("Inserisci un importo valido");
      return null;
    }
    if (!newLink.check_in_date) {
      toast.error("Data Check-in obbligatoria");
      return null;
    }
    if (!newLink.check_out_date) {
      toast.error("Data Check-out obbligatoria");
      return null;
    }
    if (newLink.check_in_date >= newLink.check_out_date) {
      toast.error("La data di Check-out deve essere successiva al Check-in");
      return null;
    }
    if (!newLink.customer_name || newLink.customer_name.trim() === "") {
      toast.error("Nome cliente obbligatorio");
      return null;
    }
    if (!newLink.adults || parseInt(newLink.adults) < 1) {
      toast.error("Inserisci almeno 1 adulto");
      return null;
    }
    
    setCreatingLink(true);
    try {
      const res = await axios.post(`${API}/admin/payments/links`, {
        ...newLink,
        amount: parseFloat(newLink.amount),
        description: emailSubject
      }, getAuthHeaders());
      toast.success("Link di pagamento creato!");
      setCreatedLink(res.data);
      if (res.data.short_url) {
        // Try to copy to clipboard (may fail on mobile without user gesture)
        try {
          await navigator.clipboard.writeText(res.data.short_url);
          toast.success("Link copiato negli appunti!");
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        } catch (clipboardError) {
          // Clipboard failed, user can copy manually
          console.log("Clipboard access denied, user can copy manually");
        }
      }
      fetchLinks();
      // Scroll to success box and focus link input after state update
      setTimeout(() => {
        if (successBoxRef.current) {
          successBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (linkInputRef.current) {
          linkInputRef.current.focus();
          linkInputRef.current.select();
        }
      }, 100);
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione del link");
      return null;
    } finally {
      setCreatingLink(false);
    }
  };
  
  // Create link and send email
  const createPaymentLinkAndSendEmail = async () => {
    if (!newLink.customer_email) {
      toast.error("Email cliente richiesta per l'invio");
      return;
    }
    
    setSendingEmail(true);
    try {
      let linkResult = createdLink;
      if (!linkResult) {
        linkResult = await createPaymentLink();
        if (!linkResult) {
          setSendingEmail(false);
          return;
        }
      }
      
      await axios.post(`${API}/admin/payments/send-email`, {
        link_id: linkResult.link_id,
        email_body: emailTemplate,
        email_subject: emailSubject,
        customer_email: newLink.customer_email,
        customer_name: newLink.customer_name,
        check_in_date: newLink.check_in_date,
        check_out_date: newLink.check_out_date
      }, getAuthHeaders());
      
      toast.success("Email inviata con successo!");
      resetForm();
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'email");
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Copy link to clipboard
  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato!");
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      // Fallback for mobile: select the text so user can copy manually
      toast.info("Seleziona e copia il link manualmente");
    }
  };
  
  // Copy link from list
  const copyLink = async (link) => {
    const urlToCopy = link.short_url || link.url;
    try {
      await navigator.clipboard.writeText(urlToCopy);
      toast.success("Link copiato!");
    } catch (error) {
      toast.info("Seleziona e copia il link manualmente");
    }
  };
  
  // Check payment status
  const checkPaymentStatus = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      toast.success(`Stato: ${res.data.payment_status}`);
      fetchLinks();
    } catch (error) {
      toast.error("Errore nel controllo dello stato");
    }
  };
  
  // Delete single link
  const deleteLink = async (linkId) => {
    setDeletingLinkId(linkId);
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteLink = async () => {
    const linkId = deletingLinkId;
    try {
      await axios.delete(`${API}/admin/payments/links/${linkId}`, getAuthHeaders());
      toast.success("Link eliminato definitivamente");
      fetchLinks();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleteDialogOpen(false);
      setDeletingLinkId(null);
    }
  };
  
  // Toggle link selection
  const toggleLink = (linkId) => {
    setSelectedLinks(prev => 
      prev.includes(linkId) 
        ? prev.filter(id => id !== linkId)
        : [...prev, linkId]
    );
  };
  
  // Toggle all links
  const toggleAllLinks = () => {
    setSelectedLinks(prev => 
      prev.length === links.length ? [] : links.map(l => l.link_id)
    );
  };
  
  // Delete selected links
  const deleteSelectedLinks = async () => {
    setDeletingLinkId(null);
    setDeletingBulk(true);
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteSelectedLinks = async () => {
    try {
      await axios.delete(`${API}/admin/payments/links/bulk`, {
        ...getAuthHeaders(),
        data: { ids: selectedLinks }
      });
      toast.success(`${selectedLinks.length} payment link eliminati definitivamente`);
      setSelectedLinks([]);
      fetchLinks();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleteDialogOpen(false);
      setDeletingBulk(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <h1 className="font-heading text-3xl mb-6">Payment Link</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="admin-tabs-list">
          <TabsTrigger 
            value="create" 
            className="admin-tab-trigger flex items-center"
          >
            <Plus size={16} className="mr-2" /> Crea Payment Link
          </TabsTrigger>
          <TabsTrigger 
            value="list" 
            className="admin-tab-trigger flex items-center"
          >
            <Link2 size={16} className="mr-2" /> Elenco Link ({links.length})
          </TabsTrigger>
          <TabsTrigger 
            value="platform" 
            className="admin-tab-trigger flex items-center"
          >
            <Settings size={16} className="mr-2" /> Platform
          </TabsTrigger>
        </TabsList>
        
        {/* Info sotto i tabs - cambia in base al tab attivo */}
        <PageInfoUnderTabs pageKey={`payment_link_${activeTab}`} />
        
        {/* TAB 1: Create Link Form */}
        <TabsContent value="create">
          <div className="bg-white rounded-xl border border-[#D4AF37] p-4 md:p-6 max-w-4xl mx-auto">
            <div className="space-y-4">
              {/* Importo e Valuta */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-1 block">Importo *</label>
                  <Input 
                    type="number" 
                    step="0.01"
                    min="0.50"
                    placeholder="100.00" 
                    value={newLink.amount} 
                    onChange={(e) => setNewLink({...newLink, amount: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-1 block">Valuta</label>
                  <select 
                    value={newLink.currency} 
                    onChange={(e) => setNewLink({...newLink, currency: e.target.value})}
                    className="w-full px-3 py-2 border border-[#D4AF37] rounded-lg h-10"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
              
              {/* Check-in / Check-out */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-1 block">Data Check-in *</label>
                  <Input 
                    type="date"
                    value={newLink.check_in_date} 
                    onChange={(e) => setNewLink({...newLink, check_in_date: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-1 block">Data Check-out *</label>
                  <Input 
                    type="date"
                    value={newLink.check_out_date} 
                    onChange={(e) => setNewLink({...newLink, check_out_date: e.target.value})} 
                  />
                </div>
              </div>
              
              {/* Nome e Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-1 block">Nome cliente *</label>
                  <Input 
                    placeholder="Mario Rossi" 
                    value={newLink.customer_name} 
                    onChange={(e) => setNewLink({...newLink, customer_name: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-1 block">Email cliente</label>
                  <Input 
                    type="email"
                    placeholder="cliente@email.com" 
                    value={newLink.customer_email} 
                    onChange={(e) => setNewLink({...newLink, customer_email: e.target.value})} 
                  />
                </div>
              </div>
              
              {/* Email Subject */}
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Oggetto Email</label>
                <Input 
                  placeholder="Reservation Payment - Suite Lucina" 
                  value={emailSubject} 
                  onChange={(e) => setEmailSubject(e.target.value)} 
                />
              </div>
              
              {/* ID Prenotazione */}
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">ID Prenotazione (opzionale)</label>
                <Input 
                  placeholder="Associa a una prenotazione" 
                  value={newLink.booking_id} 
                  onChange={(e) => setNewLink({...newLink, booking_id: e.target.value})} 
                />
              </div>
              
              {/* Dati ospiti */}
              <div className="border-t border-[#D4AF37] pt-4 mt-2">
                <p className="text-sm font-medium text-[#44403C] mb-3">Dati ospiti (obbligatorio) *</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Adulti</label>
                    <Input 
                      type="number"
                      min="0"
                      value={newLink.adults === 0 ? "" : newLink.adults} 
                      onChange={(e) => setNewLink({...newLink, adults: e.target.value === "" ? 0 : parseInt(e.target.value) || 0})}
                      onFocus={(e) => { if (newLink.adults === 0) e.target.value = ""; }}
                      onBlur={(e) => { if (e.target.value === "") setNewLink({...newLink, adults: 0}); }}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Bambini</label>
                    <Input 
                      type="number"
                      min="0"
                      value={newLink.children === 0 ? "" : newLink.children} 
                      onChange={(e) => setNewLink({...newLink, children: e.target.value === "" ? 0 : parseInt(e.target.value) || 0})}
                      onFocus={(e) => { if (newLink.children === 0) e.target.value = ""; }}
                      onBlur={(e) => { if (e.target.value === "") setNewLink({...newLink, children: 0}); }}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Infanti</label>
                    <Input 
                      type="number"
                      min="0"
                      value={newLink.infants === 0 ? "" : newLink.infants} 
                      onChange={(e) => setNewLink({...newLink, infants: e.target.value === "" ? 0 : parseInt(e.target.value) || 0})}
                      onFocus={(e) => { if (newLink.infants === 0) e.target.value = ""; }}
                      onBlur={(e) => { if (e.target.value === "") setNewLink({...newLink, infants: 0}); }}
                      placeholder="0"
                    />
                  </div>
                </div>
                {(newLink.children > 0) && (
                  <div className="mt-3">
                    <label className="text-xs text-[#78716C] mb-1 block">Età bambini (separati da virgola)</label>
                    <Input 
                      placeholder="es. 8, 12, 5" 
                      value={newLink.children_ages || ""} 
                      onChange={(e) => setNewLink({...newLink, children_ages: e.target.value})} 
                    />
                    <p className="text-xs text-[#A8A29E] mt-1">Necessario per calcolare le esenzioni</p>
                  </div>
                )}
              </div>
              
              {/* Email Template */}
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Email Template</label>
                <p className="text-xs text-[#78716C] mb-2">Variabili: (customer name), [check-in date], [check-out date], (link)</p>
                <Textarea 
                  value={emailTemplate}
                  onChange={(e) => setEmailTemplate(e.target.value)}
                  rows={7}
                  className="font-mono text-sm"
                />
              </div>
              
              {/* Email Preview */}
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block flex items-center gap-2">
                  <Eye size={14} /> Anteprima Email
                </label>
                <div className="p-3 bg-[#FAFAF9] border border-[#D4AF37] rounded-lg text-sm whitespace-pre-wrap text-[#44403C]">
                  {getEmailPreview()}
                </div>
                <p className="text-xs text-[#A8A29E] mt-1">Il link di pagamento verrà generato automaticamente</p>
              </div>
              
              {/* Created Link Display */}
              {createdLink && (
                <div ref={successBoxRef} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-medium text-green-800 mb-2">✅ Link creato con successo!</p>
                  <div className="flex items-center gap-2">
                    <input 
                      ref={linkInputRef}
                      value={createdLink.short_url} 
                      readOnly 
                      className="flex-1 bg-white px-3 py-2 border border-[#D4AF37] rounded-lg h-10"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => copyToClipboard(createdLink.short_url)}
                      className="flex items-center gap-1"
                    >
                      {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                      {copied ? "Copiato!" : "Copia"}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Mode indicator */}
              {config.mode === "test" && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">⚠️ Modalità TEST attiva - I pagamenti non saranno reali</p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4 border-t border-[#E7E5E4]">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetForm}
                  className="order-3 sm:order-1"
                >
                  Reset
                </Button>
                <Button 
                  type="button" 
                  onClick={createPaymentLink} 
                  disabled={creatingLink || sendingEmail} 
                  className="bg-[#1C1917] hover:bg-[#292524] text-white order-1 sm:order-2"
                >
                  {creatingLink ? (
                    <><RefreshCw size={16} className="animate-spin mr-2" /> Creazione...</>
                  ) : (
                    <><Link2 size={16} className="mr-2" /> Crea Link</>
                  )}
                </Button>
                <Button 
                  type="button" 
                  onClick={createPaymentLinkAndSendEmail} 
                  disabled={creatingLink || sendingEmail || !newLink.customer_email} 
                  className="bg-[#D4AF37] hover:bg-[#B8972E] text-white order-2 sm:order-3"
                  title={!newLink.customer_email ? "Email cliente richiesta" : "Crea link e invia email"}
                >
                  {sendingEmail ? (
                    <><RefreshCw size={16} className="animate-spin mr-2" /> Invio...</>
                  ) : (
                    <><Mail size={16} className="mr-2" /> Invia Mail</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* TAB 2: Links List */}
        <TabsContent value="list">
          <div className="bg-white rounded-xl border border-[#D4AF37] p-4 md:p-6">
            {/* Header with actions */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div className="flex items-center gap-4">
                {links.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={selectedLinks.length === links.length && links.length > 0} 
                      onChange={toggleAllLinks}
                      className="w-4 h-4 accent-[#D4AF37]"
                    />
                    <span className="text-sm text-[#78716C]">Seleziona tutti ({links.length})</span>
                  </label>
                )}
                {selectedLinks.length > 0 && (
                  <Button variant="destructive" size="sm" onClick={deleteSelectedLinks}>
                    <Trash2 size={14} className="mr-1" /> Elimina {selectedLinks.length}
                  </Button>
                )}
              </div>
            </div>
            
            <p className="text-sm text-[#78716C] mb-6">
              Elenco dei link di pagamento creati. Puoi copiarli, verificare lo stato o eliminarli.
            </p>
            
            {loadingLinks ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw size={24} className="animate-spin text-[#78716C]" />
              </div>
            ) : links.length === 0 ? (
              <div className="text-center py-12 text-[#78716C]">
                <Link2 size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nessun link di pagamento creato</p>
                <Button 
                  onClick={() => setActiveTab("create")} 
                  className="mt-4 bg-[#D4AF37] hover:bg-[#B8972E] text-white"
                >
                  <Plus size={16} className="mr-2" /> Crea il primo link
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#D4AF37]">
                      <th className="w-10 py-3 px-2"></th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Cliente</th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Importo</th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Check-in</th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Stato</th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Data</th>
                      <th className="text-center py-3 px-4 font-medium text-[#44403C]">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {links.map((link) => (
                      <tr 
                        key={link.link_id} 
                        className={`border-b border-[#D4AF37] hover:bg-[#FAFAF9] ${selectedLinks.includes(link.link_id) ? 'bg-[#FDF9EF]' : ''}`}
                      >
                        <td className="py-3 px-2">
                          <input 
                            type="checkbox" 
                            checked={selectedLinks.includes(link.link_id)} 
                            onChange={() => toggleLink(link.link_id)} 
                            className="w-4 h-4 accent-[#D4AF37]" 
                          />
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {link.mode === "test" && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-1 rounded">TEST</span>
                            )}
                            {link.email_sent && (
                              <span className="text-xs bg-green-100 text-green-800 px-1 rounded">EMAIL</span>
                            )}
                            <div>
                              <div className="font-medium">{link.customer_name || "-"}</div>
                              {link.customer_email && (
                                <div className="text-xs text-[#78716C]">{link.customer_email}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-medium">€{link.amount.toFixed(2)}</td>
                        <td className="py-3 px-4 text-[#78716C] text-sm">
                          {link.check_in_date || "-"}
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={link.payment_status || link.status} />
                        </td>
                        <td className="py-3 px-4 text-[#78716C] text-sm">{formatDateDisplay(link.created_at)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => copyLink(link)} 
                              title="Copia link"
                            >
                              <Copy size={16} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => window.open(link.short_url || link.url, "_blank")} 
                              title="Apri link"
                            >
                              <ExternalLink size={16} />
                            </Button>
                            {link.session_id && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => checkPaymentStatus(link.session_id)} 
                                title="Verifica stato"
                              >
                                <RefreshCw size={16} />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => deleteLink(link.link_id)} 
                              className="text-red-600 hover:text-red-700" 
                              title="Elimina"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* TAB 3: Platform Configuration */}
        <TabsContent value="platform">
          <div className="space-y-6">
            {/* STRIPE CREDENTIALS */}
            <div className="bg-white rounded-xl border-2 border-[#D4AF37] p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl">🔐 Credenziali Stripe</h2>
                {stripeCredentials && (
                  <span className={`text-sm px-3 py-1 rounded-full font-medium ${stripeCredentials.active_mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {stripeCredentials.active_mode === 'live' ? '🟢 LIVE' : '🟡 TEST'}
                  </span>
                )}
              </div>
              
              {/* Webhook URL (read-only) */}
              {stripeCredentials?.webhook_url && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="text-xs text-blue-700 uppercase tracking-wide mb-1 block font-medium">Webhook URL (configura su Stripe Dashboard)</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-white px-3 py-2 rounded border flex-1 overflow-x-auto font-mono">{stripeCredentials.webhook_url}</code>
                    <Button size="sm" variant="outline" onClick={() => {navigator.clipboard.writeText(stripeCredentials.webhook_url); toast.success("URL copiato!")}}>
                      <Copy size={14} className="mr-1" /> Copia
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Eventi richiesti: <code>checkout.session.completed</code>, <code>checkout.session.expired</code>, <code>payment_intent.payment_failed</code>
                  </p>
                </div>
              )}
              
              {/* Mode Toggle */}
              <div className="mb-6 p-4 bg-[#FAFAF9] rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#1C1917]">Modalità Pagamenti</p>
                    <p className="text-sm text-[#78716C]">
                      {config.mode === 'live' 
                        ? '🚨 I pagamenti sono REALI' 
                        : '🧪 Modalità test - nessun addebito reale'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleModeChange("test")}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${config.mode === "test" ? "bg-yellow-100 text-yellow-800 border-2 border-yellow-400" : "bg-[#F5F5F4] text-[#78716C]"}`}
                    >
                      🧪 Test
                    </button>
                    <button
                      onClick={() => handleModeChange("live")}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${config.mode === "live" ? "bg-green-100 text-green-800 border-2 border-green-400" : "bg-[#F5F5F4] text-[#78716C]"}`}
                    >
                      🚀 Live
                    </button>
                  </div>
                </div>
              </div>
              
              {/* TEST Credentials */}
              <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-medium text-yellow-800 mb-3 flex items-center gap-2">
                  <span className="bg-yellow-200 text-yellow-800 text-xs px-2 py-0.5 rounded">TEST</span>
                  Credenziali Test
                  {stripeCredentials?.has_test_credentials && <Check size={16} className="text-green-600" />}
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-yellow-700 mb-1 block">Publishable Key</label>
                    <Input 
                      value={stripeTestPublishable} 
                      onChange={(e) => setStripeTestPublishable(e.target.value)}
                      placeholder={stripeCredentials?.publishable_key_test || "pk_test_..."}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-yellow-700 mb-1 block">Secret Key</label>
                    <Input 
                      type="password"
                      value={stripeTestSecret} 
                      onChange={(e) => setStripeTestSecret(e.target.value)}
                      placeholder={stripeCredentials?.secret_key_test_masked || "sk_test_..."}
                    />
                    {stripeCredentials?.secret_key_test_masked && (
                      <p className="text-xs text-yellow-600 mt-1">Attuale: {stripeCredentials.secret_key_test_masked}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-yellow-700 mb-1 block">Webhook Signing Secret</label>
                    <Input 
                      type="password"
                      value={stripeTestWebhook} 
                      onChange={(e) => setStripeTestWebhook(e.target.value)}
                      placeholder={stripeCredentials?.webhook_secret_test_masked || "whsec_..."}
                    />
                    {stripeCredentials?.webhook_secret_test_masked && (
                      <p className="text-xs text-yellow-600 mt-1">Attuale: {stripeCredentials.webhook_secret_test_masked}</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* LIVE Credentials */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                  <span className="bg-green-200 text-green-800 text-xs px-2 py-0.5 rounded">LIVE</span>
                  Credenziali Live
                  {stripeCredentials?.has_live_credentials && <Check size={16} className="text-green-600" />}
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-green-700 mb-1 block">Publishable Key</label>
                    <Input 
                      value={stripeLivePublishable} 
                      onChange={(e) => setStripeLivePublishable(e.target.value)}
                      placeholder={stripeCredentials?.publishable_key_live || "pk_live_..."}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-green-700 mb-1 block">Secret Key</label>
                    <Input 
                      type="password"
                      value={stripeLiveSecret} 
                      onChange={(e) => setStripeLiveSecret(e.target.value)}
                      placeholder={stripeCredentials?.secret_key_live_masked || "sk_live_..."}
                    />
                    {stripeCredentials?.secret_key_live_masked && (
                      <p className="text-xs text-green-600 mt-1">Attuale: {stripeCredentials.secret_key_live_masked}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-green-700 mb-1 block">Webhook Signing Secret</label>
                    <Input 
                      type="password"
                      value={stripeLiveWebhook} 
                      onChange={(e) => setStripeLiveWebhook(e.target.value)}
                      placeholder={stripeCredentials?.webhook_secret_live_masked || "whsec_..."}
                    />
                    {stripeCredentials?.webhook_secret_live_masked && (
                      <p className="text-xs text-green-600 mt-1">Attuale: {stripeCredentials.webhook_secret_live_masked}</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Save Credentials Button */}
              <div className="flex justify-end mb-4">
                <Button 
                  onClick={saveStripeCredentials}
                  disabled={savingCredentials || (!stripeTestPublishable && !stripeTestSecret && !stripeTestWebhook && !stripeLivePublishable && !stripeLiveSecret && !stripeLiveWebhook)}
                  className="bg-[#D4AF37] hover:bg-[#B8972E] text-white"
                >
                  {savingCredentials ? <><RefreshCw size={16} className="animate-spin mr-2" /> Salvataggio...</> : <><Save size={16} className="mr-2" /> Salva Credenziali</>}
                </Button>
              </div>
              
              {/* Warning */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  ⚠️ <strong>Importante:</strong> Le Secret Key e i Webhook Secret non sono visualizzabili dopo il salvataggio. Conserva una copia sicura delle tue chiavi.
                </p>
              </div>
            </div>

            {/* Other Config */}
            <div className="bg-white rounded-xl border border-[#D4AF37] p-4 md:p-6">
              <h2 className="font-heading text-xl mb-6">Altre Impostazioni</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-2 block">Valuta</label>
                  <select 
                    value={config.currency} 
                    onChange={(e) => setConfig({...config, currency: e.target.value})}
                    className="w-full px-3 py-2 border border-[#D4AF37] rounded-lg"
                  >
                    <option value="EUR">EUR - Euro</option>
                    <option value="USD">USD - Dollaro USA</option>
                    <option value="GBP">GBP - Sterlina</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-2 block">Paese</label>
                  <select 
                    value={config.country} 
                    onChange={(e) => setConfig({...config, country: e.target.value})}
                    className="w-full px-3 py-2 border border-[#D4AF37] rounded-lg"
                  >
                    <option value="IT">Italia</option>
                    <option value="US">Stati Uniti</option>
                    <option value="GB">Regno Unito</option>
                    <option value="DE">Germania</option>
                    <option value="FR">Francia</option>
                  </select>
                </div>
              </div>

              {/* Notification Email */}
              <div className="mb-6">
                <label className="text-sm font-medium text-[#44403C] mb-2 block">Email notifiche pagamenti</label>
                <Input 
                  type="email"
                  value={config.notification_email || ""} 
                  onChange={(e) => setConfig({...config, notification_email: e.target.value})}
                  placeholder="pagamenti@tuodominio.com"
                />
              </div>

              <Button onClick={saveConfig} disabled={savingConfig} className="bg-[#1C1917] hover:bg-[#292524] text-white">
                {savingConfig ? <><RefreshCw size={16} className="animate-spin mr-2" /> Salvataggio...</> : <><Save size={16} className="mr-2" /> Salva Configurazione</>}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deletingBulk ? "Elimina Payment Link Selezionati" : "Elimina Payment Link"}
        description={deletingBulk 
          ? `Stai per eliminare ${selectedLinks.length} payment link e tutti i dati associati.`
          : "Stai per eliminare questo payment link e tutti i dati associati."
        }
        itemCount={deletingBulk ? `${selectedLinks.length} payment link selezionati` : null}
        onConfirm={deletingBulk ? confirmDeleteSelectedLinks : confirmDeleteLink}
      />
      
      {/* Generic Confirmation Modal (non-destructive) */}
      <ConfirmModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={confirmModalConfig.onConfirm}
        title={confirmModalConfig.title}
        message={confirmModalConfig.message}
        variant={confirmModalConfig.variant}
        confirmText="Conferma"
        cancelText="Annulla"
      />
    </div>
  );
}
